import { memo, useMemo } from 'react';

import { type ElementInfo } from '~/components/DevModal/types';
import { parseCSSValue } from './utils';

type BoxModelOverlaysProps = {
  elementInfo: ElementInfo;
};

export const BoxModelOverlays = memo(({ elementInfo }: BoxModelOverlaysProps) => {
  // Memoize parsed margin values
  const marginValues = useMemo(
    () => ({
      top: parseCSSValue(elementInfo.styles.marginTop || '0'),
      right: parseCSSValue(elementInfo.styles.marginRight || '0'),
      bottom: parseCSSValue(elementInfo.styles.marginBottom || '0'),
      left: parseCSSValue(elementInfo.styles.marginLeft || '0'),
    }),
    [
      elementInfo.styles.marginTop,
      elementInfo.styles.marginRight,
      elementInfo.styles.marginBottom,
      elementInfo.styles.marginLeft,
    ],
  );

  // Memoize parsed padding values
  const paddingValues = useMemo(
    () => ({
      top: parseCSSValue(elementInfo.styles.paddingTop || '0'),
      right: parseCSSValue(elementInfo.styles.paddingRight || '0'),
      bottom: parseCSSValue(elementInfo.styles.paddingBottom || '0'),
      left: parseCSSValue(elementInfo.styles.paddingLeft || '0'),
    }),
    [
      elementInfo.styles.paddingTop,
      elementInfo.styles.paddingRight,
      elementInfo.styles.paddingBottom,
      elementInfo.styles.paddingLeft,
    ],
  );

  // Memoize border width
  const borderWidth = useMemo(
    () => parseCSSValue(elementInfo.styles.borderWidth || '0'),
    [elementInfo.styles.borderWidth],
  );

  return (
    <>
      {/* Margin (outermost) - Purple - Only show if there's actual margin */}
      {(marginValues.top > 0 || marginValues.right > 0 || marginValues.bottom > 0 || marginValues.left > 0) && (
        <div
          className="pointer-events-none fixed z-[9995] border-2 border-purple-6 bg-purple-6/20"
          style={{
            left: elementInfo.position.x - marginValues.left,
            top: elementInfo.position.y - marginValues.top,
            width: elementInfo.dimensions.width + marginValues.right + marginValues.left,
            height: elementInfo.dimensions.height + marginValues.top + marginValues.bottom,
          }}
        />
      )}

      {/* Border - Red */}
      <div
        className="pointer-events-none fixed z-[9996] border-2 border-red-5 bg-red-5/10"
        style={{
          left: elementInfo.position.x,
          top: elementInfo.position.y,
          width: elementInfo.dimensions.width,
          height: elementInfo.dimensions.height,
        }}
      />

      {/* Padding - Orange - Only show if there's actual padding */}
      {(paddingValues.top > 0 || paddingValues.right > 0 || paddingValues.bottom > 0 || paddingValues.left > 0) && (
        <div
          className="pointer-events-none fixed z-[9997] border-2 border-orange-5 bg-orange-5/20"
          style={{
            left: elementInfo.position.x + borderWidth,
            top: elementInfo.position.y + borderWidth,
            width: elementInfo.dimensions.width - borderWidth * 2,
            height: elementInfo.dimensions.height - borderWidth * 2,
          }}
        />
      )}

      {/* Content - Blue */}
      <div
        className="pointer-events-none fixed z-[9998] border-2 border-blue-5 bg-blue-5/30"
        style={{
          left: elementInfo.position.x + borderWidth + paddingValues.left,
          top: elementInfo.position.y + borderWidth + paddingValues.top,
          width: elementInfo.dimensions.width - borderWidth * 2 - paddingValues.right - paddingValues.left,
          height: elementInfo.dimensions.height - borderWidth * 2 - paddingValues.top - paddingValues.bottom,
        }}
      />
    </>
  );
});

BoxModelOverlays.displayName = 'BoxModelOverlays';
